<script>
/**
 * 회원 상세 페이지.
 *
 * 4개 섹션으로 구분하여 표시:
 *   ① 기본 정보    - user 테이블 (이름, 연락처, 본인인증, 가입일, 추천지점 등)
 *   ② 로그인 정보  - user_site (이메일) + user_oauth (소셜 provider 목록)
 *   ③ 계좌 정보    - user_account (은행명, 계좌번호, 예금주)
 *   ④ 보유 자산    - user_asset (KRW 현금, POINT 포인트)
 *
 * 기능:
 *   - 수정 모드: 이름·연락처·추천지점코드 인라인 수정
 *   - 자산 입력: MemberAssetDialog를 통한 현금/포인트 upsert
 *   - 삭제: 확인 다이얼로그 후 soft delete → 목록으로 이동
 */
import { getMemberDetail, updateMember, deleteMember } from '@/api/member.js'
import { getAppraisalList } from '@/api/appraisal.js'
import { getProductWithdrawalList } from '@/api/productWithdrawal.js'
import { getTradeList } from '@/api/trade.js'
import MemberAssetDialog from './MemberAssetDialog.vue'

export default {
  name: 'MemberDetailPage',

  components: {
    MemberAssetDialog,
  },

  data() {
    return {
      /** 회원 상세 데이터 (API 응답 data 필드) */
      member: null,

      /** 페이지 로딩 상태 */
      loading: false,

      /** 수정 모드 여부 */
      editMode: false,

      /** 수정 폼 데이터 (수정 모드 진입 시 현재 값으로 초기화) */
      editForm: {
        name: '',
        phone: '',
        branchCode: '',
      },

      /** 수정 API 호출 중 */
      saving: false,

      /** 수정 에러 메시지 */
      editError: '',

      /** 삭제 확인 다이얼로그 표시 여부 */
      deleteDialog: false,

      /** 삭제 API 호출 중 */
      deleting: false,

      /** 자산 입력 다이얼로그 표시 여부 */
      assetDialog: false,

      /** 소셜 provider 표시 레이블 맵 */
      providerLabelMap: {
        KAKAO: '카카오',
        NAVER: '네이버',
        GOOGLE: '구글',
      },

      /** 소셜 provider 칩 색상 맵 */
      providerColorMap: {
        KAKAO: 'warning',
        NAVER: 'success',
        GOOGLE: 'error',
      },

      /** 계좌 유형 한국어 레이블 맵 */
      accountTypeLabelMap: {
        WITHDRAW: '출금',
        DEPOSIT: '입금',
      },

      /** 회원 활동 요약(최근 5건) */
      recentAppraisals: [],
      recentPurchases: [],
      recentTradeSells: [],
      recentTradeBuys: [],
      recentHistoryLoading: false,
    }
  },

  computed: {
    /** URL 파라미터에서 userCode 추출 */
    userCode() {
      return this.$route.params.userCode
    },

    /** 보유 현금 (KRW) */
    krwAsset() {
      return this.member?.assets?.find(a => a.assetType === 'KRW')?.assetAmount ?? 0
    },

    /** 보유 포인트 */
    pointAsset() {
      return this.member?.assets?.find(a => a.assetType === 'POINT')?.assetAmount ?? 0
    },
    /** 금 시세평가 자산 */
    goldMarketAsset() {
      return this.member?.metalSummary?.goldMarketAmount ?? 0
    },
    /** 은 시세평가 자산 */
    silverMarketAsset() {
      return this.member?.metalSummary?.silverMarketAmount ?? 0
    },
    goldWeightG() {
      return this.member?.metalSummary?.goldWeightG ?? 0
    },
    silverWeightG() {
      return this.member?.metalSummary?.silverWeightG ?? 0
    },

    /** 수정 모드의 이름 유효성 검사 규칙 */
    nameRules() {
      return [
        v => !!v?.trim() || '이름은 필수입니다.',
        v => v.length <= 100 || '이름은 100자 이내로 입력해 주세요.',
      ]
    },

    /** 수정 모드의 연락처 유효성 검사 규칙 */
    phoneRules() {
      return [
        v => !v || /^[0-9\-]{0,20}$/.test(v) || '연락처 형식이 올바르지 않습니다.',
      ]
    },
  },

  mounted() {
    this.fetchDetail()
  },

  methods: {
    /**
     * 회원 상세 정보 조회.
     */
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getMemberDetail(this.userCode)
        if (res.data?.code === 0) {
          this.member = res.data.data
          await this.fetchRecentHistories()
        } else {
          // 조회 실패 시 목록으로 이동
          this.$router.replace('/members')
        }
      } catch (e) {
        console.error('[MemberDetailPage] fetchDetail error:', e)
        this.$router.replace('/members')
      } finally {
        this.loading = false
      }
    },
    async fetchRecentHistories() {
      if (!this.member) return
      this.recentHistoryLoading = true
      try {
        const keywordByName = this.member?.name || undefined
        const keywordByCode = this.member?.userCode || undefined
        const range = this.getWideHistoryRange()
        const [appraisalResByCode, purchaseRes, tradeSellRes, tradeBuyRes] = await Promise.all([
          getAppraisalList({ keyword: keywordByCode, startDate: range.startDate, endDate: range.endDate, page: 1, size: 5 }).catch(() => null),
          getProductWithdrawalList({ keyword: keywordByCode, startDate: range.startDate, endDate: range.endDate, page: 1, size: 5 }).catch(() => null),
          getTradeList({ keyword: keywordByCode, tradeSide: 'SELL', startDate: range.startDate, endDate: range.endDate, page: 1, size: 5 }).catch(() => null),
          getTradeList({ keyword: keywordByCode, tradeSide: 'BUY', startDate: range.startDate, endDate: range.endDate, page: 1, size: 5 }).catch(() => null),
        ])
        let appraisals = appraisalResByCode?.data?.code === 0 ? (appraisalResByCode.data?.data?.items || []) : []
        if (appraisals.length === 0 && keywordByName) {
          const appraisalResByName = await getAppraisalList({
            keyword: keywordByName,
            startDate: range.startDate,
            endDate: range.endDate,
            page: 1,
            size: 5,
          }).catch(() => null)
          appraisals = appraisalResByName?.data?.code === 0 ? (appraisalResByName.data?.data?.items || []) : []
        }

        this.recentAppraisals = appraisals
        this.recentPurchases = purchaseRes?.data?.code === 0 ? (purchaseRes.data?.data?.items || []) : []
        this.recentTradeSells = tradeSellRes?.data?.code === 0 ? (tradeSellRes.data?.data?.items || []) : []
        this.recentTradeBuys = tradeBuyRes?.data?.code === 0 ? (tradeBuyRes.data?.data?.items || []) : []
      } finally {
        this.recentHistoryLoading = false
      }
    },
    getWideHistoryRange() {
      const toDate = (d) => d.toISOString().substring(0, 10)
      const today = new Date()
      const start = new Date(today)
      start.setFullYear(start.getFullYear() - 5)
      const end = new Date(today)
      end.setFullYear(end.getFullYear() + 1)
      return { startDate: toDate(start), endDate: toDate(end) }
    },

    /**
     * 수정 모드 진입.
     * 현재 회원 정보를 editForm에 복사한다.
     */
    enterEditMode() {
      this.editForm = {
        name:       this.member.name || '',
        phone:      this.member.phone || '',
        branchCode: this.member.branchCode || '',
      }
      this.editError = ''
      this.editMode = true
    },

    /** 수정 모드 취소 */
    cancelEdit() {
      this.editMode = false
      this.editError = ''
    },

    /**
     * 회원 정보 저장.
     * 유효성 검사 후 PUT API 호출, 성공 시 상세 재조회.
     */
    async handleSave() {
      const { valid } = await this.$refs.editForm.validate()
      if (!valid) return

      this.saving = true
      this.editError = ''
      try {
        const res = await updateMember(this.userCode, {
          name:       this.editForm.name,
          phone:      this.editForm.phone || undefined,
          branchCode: this.editForm.branchCode || undefined,
        })
        if (res.data?.code === 0) {
          this.member = res.data.data
          this.editMode = false
        } else {
          this.editError = res.data?.message || '수정에 실패했습니다.'
        }
      } catch (e) {
        console.error('[MemberDetailPage] handleSave error:', e)
        this.editError = '서버 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    /**
     * 회원 삭제 실행 (soft delete).
     * 성공 시 목록 페이지로 이동한다.
     */
    async handleDelete() {
      this.deleting = true
      try {
        const res = await deleteMember(this.userCode)
        if (res.data?.code === 0) {
          this.deleteDialog = false
          this.$router.push('/members')
        } else {
          this.deleteDialog = false
          alert(res.data?.message || '삭제에 실패했습니다.')
        }
      } catch (e) {
        console.error('[MemberDetailPage] handleDelete error:', e)
        alert('서버 오류가 발생했습니다.')
      } finally {
        this.deleting = false
      }
    },

    /** 숫자를 천 단위 콤마 형식으로 변환 */
    formatNumber(num) {
      if (num == null) return '0'
      return num.toLocaleString('ko-KR')
    },

    /** 날짜시간 문자열을 보기 좋게 포맷 (YYYY-MM-DD HH:mm) */
    formatDt(dt) {
      if (!dt) return '-'
      return dt.substring(0, 16).replace('T', ' ')
    },

    /** 날짜 문자열 포맷 (YYYY-MM-DD) */
    formatDate(dt) {
      if (!dt) return '-'
      return dt.substring(0, 10)
    },
    formatWeight(value) {
      if (value == null || value === '') return '0'
      const n = Number(value)
      if (!Number.isFinite(n)) return String(value)
      return n.toLocaleString('ko-KR', { maximumFractionDigits: 3 })
    },
    appraisalWeightSummary(item) {
      const parts = []
      const gold = Number(item?.goldTotalWeightG)
      const silver = Number(item?.silverTotalWeightG)
      if (Number.isFinite(gold) && gold > 0) parts.push(`금 ${this.formatWeight(gold)}g`)
      if (Number.isFinite(silver) && silver > 0) parts.push(`은 ${this.formatWeight(silver)}g`)
      return parts.length ? parts.join(' / ') : '-'
    },
    moveToAppraisalList() {
      const range = this.getWideHistoryRange()
      this.$router.push({
        path: '/appraisal',
        query: { keyword: this.member?.userCode || '', startDate: range.startDate, endDate: range.endDate },
      })
    },
    moveToOrderStatusList() {
      const range = this.getWideHistoryRange()
      this.$router.push({
        path: '/order-status',
        query: { keyword: this.member?.userCode || '', startDate: range.startDate, endDate: range.endDate },
      })
    },
    moveToTradeList(tradeSide) {
      const range = this.getWideHistoryRange()
      this.$router.push({
        path: '/trade',
        query: { keyword: this.member?.userCode || '', tradeSide, startDate: range.startDate, endDate: range.endDate },
      })
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="breadcrumb-link text-primary" @click="$router.push('/members')">회원관리</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상세</span>
        </div>
        <div class="text-h5 font-weight-bold">회원 상세</div>
        <div class="text-body-2 text-grey mt-1">회원 정보 조회 및 수정</div>
      </div>

      <!-- 조회 모드 액션 -->
      <div v-if="!editMode && member" class="d-flex ga-2">
        <v-btn
          variant="tonal"
          color="primary"
          rounded="lg"
          prepend-icon="mdi-pencil-outline"
          :disabled="member.deleteYn === 'Y'"
          @click="enterEditMode"
        >
          수정
        </v-btn>
        <v-btn
          variant="tonal"
          color="error"
          rounded="lg"
          prepend-icon="mdi-account-remove-outline"
          :disabled="member.deleteYn === 'Y'"
          @click="deleteDialog = true"
        >
          회원 삭제
        </v-btn>
      </div>

      <!-- 수정 모드 액션 -->
      <div v-if="editMode" class="d-flex ga-2">
        <v-btn variant="text" :disabled="saving" @click="cancelEdit">취소</v-btn>
        <v-btn color="primary" variant="flat" rounded="lg" :loading="saving" prepend-icon="mdi-check" @click="handleSave">
          저장
        </v-btn>
      </div>
    </div>

    <!-- 로딩 스켈레톤 -->
    <v-row v-if="loading">
      <v-col v-for="i in 4" :key="i" cols="12" md="6">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- 상세 내용 -->
    <template v-if="!loading && member">
      <v-row>

        <!-- ① 기본 정보 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card" height="100%">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-primary-soft">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">기본 정보</span>
              <v-spacer />
              <v-chip
                v-if="member.deleteYn === 'Y'"
                size="x-small"
                color="error"
                label
                variant="flat"
                class="font-weight-bold"
              >
                탈퇴
              </v-chip>
            </div>
            <v-divider />

            <!-- 수정 모드 -->
            <v-card-text v-if="editMode" class="pa-5">
              <v-form ref="editForm">
                <div class="edit-field-group">
                  <div class="field-label">이름 *</div>
                  <v-text-field
                    v-model="editForm.name"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    :rules="nameRules"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">연락처</div>
                  <v-text-field
                    v-model="editForm.phone"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    :rules="phoneRules"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">추천지점코드</div>
                  <v-text-field
                    v-model="editForm.branchCode"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    placeholder="비워두면 해제됩니다"
                    hide-details="auto"
                  />
                </div>
                <v-alert
                  v-if="editError"
                  type="error"
                  variant="tonal"
                  density="compact"
                  rounded="lg"
                  class="mt-2"
                >
                  {{ editError }}
                </v-alert>
              </v-form>
            </v-card-text>

            <!-- 조회 모드 -->
            <v-card-text v-else class="pa-5">
              <v-alert
                v-if="member.deleteYn === 'Y'"
                type="warning"
                variant="tonal"
                density="compact"
                rounded="lg"
                class="mb-4"
              >
                탈퇴된 회원입니다. ({{ formatDate(member.deleteDt) }})
              </v-alert>
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">회원코드</div>
                  <div class="dl-value text-caption text-medium-emphasis">{{ member.userCode }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">이름</div>
                  <div class="dl-value font-weight-bold">{{ member.name || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">연락처</div>
                  <div class="dl-value">{{ member.phone || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">추천지점</div>
                  <div class="dl-value">
                    <template v-if="member.branchName && member.branchName !== '-'">
                      <span class="font-weight-medium">{{ member.branchName }}</span>
                      <span class="text-caption text-medium-emphasis ml-1">({{ member.branchCode }})</span>
                    </template>
                    <span v-else class="text-medium-emphasis">-</span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">본인인증</div>
                  <div class="dl-value d-flex align-center ga-2">
                    <v-chip
                      :color="member.authYn === 'Y' ? 'success' : 'grey'"
                      size="x-small"
                      label
                      variant="tonal"
                      class="font-weight-medium"
                    >
                      {{ member.authYn === 'Y' ? '완료' : '미완료' }}
                    </v-chip>
                    <span v-if="member.authYn === 'Y'" class="text-caption text-medium-emphasis">
                      {{ formatDt(member.authDt) }}
                    </span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">결제비밀번호</div>
                  <div class="dl-value">
                    <v-chip
                      :color="member.paymentPwYn === 'Y' ? 'success' : 'grey'"
                      size="x-small"
                      label
                      variant="tonal"
                      class="font-weight-medium"
                    >
                      {{ member.paymentPwYn === 'Y' ? '설정됨' : '미설정' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">가입일</div>
                  <div class="dl-value text-medium-emphasis">{{ formatDt(member.insertDt) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">최종수정</div>
                  <div class="dl-value text-medium-emphasis">{{ formatDt(member.updateDt) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ② 로그인 정보 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card" height="100%">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-indigo-soft">
                <v-icon color="indigo" size="18">mdi-login-variant</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">로그인 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <!-- 사이트 로그인 -->
              <div class="login-section-label">
                <v-icon size="14" class="mr-1 text-medium-emphasis">mdi-email-outline</v-icon>
                사이트 로그인
              </div>
              <div class="login-content mb-4">
                <div v-if="member.siteEmail" class="d-flex align-center ga-2">
                  <v-chip color="primary" size="small" variant="tonal" label class="font-weight-medium">
                    <v-icon start size="13">mdi-check-circle-outline</v-icon>
                    연동됨
                  </v-chip>
                  <span class="text-body-2 font-weight-medium">{{ member.siteEmail }}</span>
                </div>
                <v-chip v-else color="grey" size="small" variant="tonal" label>미연동</v-chip>
              </div>

              <v-divider class="mb-4" />

              <!-- 소셜 로그인 -->
              <div class="login-section-label">
                <v-icon size="14" class="mr-1 text-medium-emphasis">mdi-account-network-outline</v-icon>
                소셜 로그인
              </div>
              <div class="login-content">
                <div
                  v-if="member.oauthList && member.oauthList.length > 0"
                  class="d-flex flex-column ga-3"
                >
                  <div
                    v-for="oauth in member.oauthList"
                    :key="oauth.provider"
                    class="oauth-item d-flex align-center ga-3"
                  >
                    <v-chip
                      :color="providerColorMap[oauth.provider] || 'default'"
                      size="small"
                      variant="tonal"
                      label
                      class="oauth-chip font-weight-bold"
                    >
                      {{ providerLabelMap[oauth.provider] || oauth.provider }}
                    </v-chip>
                    <div class="flex-grow-1">
                      <div class="text-body-2 font-weight-medium">{{ oauth.email || '-' }}</div>
                      <div class="text-caption text-medium-emphasis">연결일: {{ formatDate(oauth.connectedDt) }}</div>
                    </div>
                  </div>
                </div>
                <v-chip v-else color="grey" size="small" variant="tonal" label>미연동</v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ③ 계좌 정보 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card" height="100%">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-teal-soft">
                <v-icon color="teal" size="18">mdi-bank-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">계좌 정보</span>
              <v-spacer />
              <v-chip
                v-if="member.accounts && member.accounts.length > 0"
                size="x-small"
                color="teal"
                label
                variant="tonal"
                class="font-weight-bold"
              >
                {{ member.accounts.length }}개
              </v-chip>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div
                v-if="member.accounts && member.accounts.length > 0"
                class="d-flex flex-column ga-3"
              >
                <div
                  v-for="(account, idx) in member.accounts"
                  :key="idx"
                  class="account-card"
                >
                  <div class="d-flex align-center justify-space-between mb-2">
                    <v-chip
                      size="x-small"
                      :color="account.accountType === 'WITHDRAW' ? 'error' : 'info'"
                      variant="tonal"
                      label
                      class="font-weight-bold"
                    >
                      {{ accountTypeLabelMap[account.accountType] || account.accountType }}
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">{{ formatDate(account.insertDt) }}</span>
                  </div>
                  <div class="font-weight-bold text-body-2 mb-1">{{ account.bankName }}</div>
                  <div class="text-body-2 text-medium-emphasis account-no">{{ account.accountNo }}</div>
                  <div class="text-caption text-medium-emphasis mt-1">예금주: {{ account.accountName }}</div>
                </div>
              </div>
              <div v-else class="empty-state">
                <v-icon size="36" color="grey-lighten-2">mdi-bank-off-outline</v-icon>
                <div class="text-caption text-medium-emphasis mt-2">등록된 계좌가 없습니다.</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ④ 보유 자산 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card" height="100%">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-amber-soft">
                <v-icon color="amber-darken-2" size="18">mdi-wallet-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">보유 자산</span>
              <v-spacer />
              <!-- 자산입력 버튼: 탈퇴 회원은 비활성화 -->
              <v-btn
                size="small"
                variant="tonal"
                color="amber-darken-2"
                rounded="lg"
                prepend-icon="mdi-pencil-plus-outline"
                :disabled="member.deleteYn === 'Y'"
                @click="assetDialog = true"
              >
                자산입력
              </v-btn>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="asset-grid">
                  <!-- 금 자산 -->
                  <div class="asset-card asset-card--gold">
                      <v-icon color="amber-lighten-1" size="24" class="mb-3">mdi-gold</v-icon>
                      <div class="asset-label">금 자산</div>
                      <div class="asset-amount asset-amount--gold">
                          {{ goldWeightG }}<span class="asset-unit">g</span>
                      </div>
                      <div class="asset-subtext">평가금액 {{ formatNumber(goldMarketAsset) }}원</div>
                  </div>
                  <!-- 은 자산 -->
                  <div class="asset-card asset-card--blue">
                      <v-icon color="blue-lighten-1" size="24" class="mb-3">mdi-silverware</v-icon>
                      <div class="asset-label">은 자산</div>
                      <div class="asset-amount asset-amount--silver">
                          {{ silverWeightG }}<span class="asset-unit">g</span>
                      </div>
                      <div class="asset-subtext">평가금액 {{ formatNumber(silverMarketAsset) }}원</div>
                  </div>
                <!-- 보유 현금 -->
                <div class="asset-card asset-card--gold">
                  <v-icon color="primary" size="24" class="mb-3">mdi-cash-multiple</v-icon>
                  <div class="asset-label">보유 현금</div>
                  <div class="asset-amount text-primary">
                    {{ formatNumber(krwAsset) }}<span class="asset-unit">원</span>
                  </div>
                </div>
                <!-- 보유 포인트 -->
                <div class="asset-card asset-card--blue">
                  <v-icon color="blue-darken-1" size="24" class="mb-3">mdi-star-circle-outline</v-icon>
                  <div class="asset-label">보유 포인트</div>
                  <div class="asset-amount text-blue-darken-1">
                    {{ formatNumber(pointAsset) }}<span class="asset-unit">P</span>
                  </div>
                </div>
                
              </div>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>

      <v-row class="mt-1">
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center px-5 py-4">
              <span class="text-subtitle-2 font-weight-bold">감정평가 (최근 5건)</span>
              <v-spacer />
              <v-btn size="small" variant="text" color="primary" @click="moveToAppraisalList">상세보기</v-btn>
            </div>
            <v-divider />
            <v-table density="compact" class="recent-table">
              <thead>
                <tr>
                  <th class="text-left">지점</th>
                  <th class="text-left">예약일</th>
                  <th class="text-left">상태</th>
                  <th class="text-left">감정 총 중량</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in recentAppraisals" :key="`app-${idx}`">
                  <td>{{ item.branchName || '-' }}</td>
                  <td>{{ formatDate(item.bookingDate) }}</td>
                  <td>{{ item.statusLabel || item.status || '-' }}</td>
                  <td>{{ appraisalWeightSummary(item) }}</td>
                </tr>
                <tr v-if="!recentHistoryLoading && recentAppraisals.length === 0">
                  <td colspan="4" class="text-center text-medium-emphasis">내역이 없습니다.</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center px-5 py-4">
              <span class="text-subtitle-2 font-weight-bold">구매 (최근 5건)</span>
              <v-spacer />
              <v-btn size="small" variant="text" color="primary" @click="moveToOrderStatusList">상세보기</v-btn>
            </div>
            <v-divider />
            <v-table density="compact" class="recent-table">
              <thead>
                <tr>
                  <th class="text-left">일시</th>
                  <th class="text-left">현물</th>
                  <th class="text-right">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in recentPurchases" :key="`buy-${idx}`">
                  <td>{{ formatDt(item.insertDt) }}</td>
                  <td>{{ item.spotTypeLabel || item.spotType || '-' }}</td>
                  <td class="text-right">{{ item.price != null ? `${formatNumber(item.price)}원` : '-' }}</td>
                </tr>
                <tr v-if="!recentHistoryLoading && recentPurchases.length === 0">
                  <td colspan="3" class="text-center text-medium-emphasis">내역이 없습니다.</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-1">
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center px-5 py-4">
              <span class="text-subtitle-2 font-weight-bold">호가거래 매도 (최근 5건)</span>
              <v-spacer />
              <v-btn size="small" variant="text" color="primary" @click="moveToTradeList('SELL')">상세보기</v-btn>
            </div>
            <v-divider />
            <v-table density="compact" class="recent-table">
              <thead>
                <tr>
                  <th class="text-left">일시</th>
                  <th class="text-left">현물</th>
                  <th class="text-right">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in recentTradeSells" :key="`sell-${idx}`">
                  <td>{{ formatDt(item.insertDt) }}</td>
                  <td>{{ item.spotTypeLabel || item.spotType || '-' }}</td>
                  <td class="text-right">{{ item.tradeAmount != null ? `${formatNumber(item.tradeAmount)}원` : '-' }}</td>
                </tr>
                <tr v-if="!recentHistoryLoading && recentTradeSells.length === 0">
                  <td colspan="3" class="text-center text-medium-emphasis">내역이 없습니다.</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center px-5 py-4">
              <span class="text-subtitle-2 font-weight-bold">호가거래 매수 (최근 5건)</span>
              <v-spacer />
              <v-btn size="small" variant="text" color="primary" @click="moveToTradeList('BUY')">상세보기</v-btn>
            </div>
            <v-divider />
            <v-table density="compact" class="recent-table">
              <thead>
                <tr>
                  <th class="text-left">일시</th>
                  <th class="text-left">현물</th>
                  <th class="text-right">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in recentTradeBuys" :key="`buy-${idx}`">
                  <td>{{ formatDt(item.insertDt) }}</td>
                  <td>{{ item.spotTypeLabel || item.spotType || '-' }}</td>
                  <td class="text-right">{{ item.tradeAmount != null ? `${formatNumber(item.tradeAmount)}원` : '-' }}</td>
                </tr>
                <tr v-if="!recentHistoryLoading && recentTradeBuys.length === 0">
                  <td colspan="3" class="text-center text-medium-emphasis">내역이 없습니다.</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- 자산 입력 다이얼로그 -->
    <MemberAssetDialog
      v-if="member"
      v-model="assetDialog"
      :user-code="userCode"
      :current-krw="krwAsset"
      :current-point="pointAsset"
      @updated="fetchDetail"
    />

    <!-- 회원 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card rounded="xl" elevation="4">
        <div class="px-6 pt-5 pb-4 d-flex align-center ga-2">
          <div class="header-icon-wrap bg-error-soft">
            <v-icon color="error" size="18">mdi-alert-circle-outline</v-icon>
          </div>
          <span class="text-subtitle-1 font-weight-bold">회원 삭제 확인</span>
        </div>
        <v-divider />
        <v-card-text class="pa-6">
          <div class="text-body-2 mb-2">
            <strong class="text-error">{{ member?.name }}</strong> 회원을 삭제하시겠습니까?
          </div>
          <div class="text-caption text-medium-emphasis">
            삭제 후에도 데이터는 유지되며, 관리자 페이지에서 탈퇴 처리된 회원으로 표시됩니다.
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">취소</v-btn>
          <v-btn
            color="error"
            variant="flat"
            rounded="lg"
            :loading="deleting"
            prepend-icon="mdi-delete-outline"
            @click="handleDelete"
          >
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
/* ── 공통 카드 ── */
.detail-card {
  border: 1px solid rgba(201, 169, 98, 0.16) !important;
}

.detail-card-header {
  background: rgba(201, 169, 98, 0.05);
}

/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}
.bg-primary-soft { background: rgba(var(--v-theme-primary), 0.1); }
.bg-indigo-soft  { background: rgba(63, 81, 181, 0.1); }
.bg-teal-soft    { background: rgba(0, 150, 136, 0.1); }
.bg-amber-soft   { background: rgba(255, 193, 7, 0.12); }
.bg-error-soft   { background: rgba(var(--v-theme-error), 0.1); }

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 기본 정보 정의 리스트 ── */
.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 16px;
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: rgba(201, 181, 139, 0.75);
  text-transform: uppercase;
}

.dl-value {
  font-size: 0.875rem;
  color: #efe5cf;
  line-height: 1.4;
}

/* ── 수정 폼 ── */
.edit-field-group {
  margin-bottom: 16px;
}

.field-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(201, 181, 139, 0.72);
  margin-bottom: 6px;
}

/* ── 로그인 정보 ── */
.login-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(201, 181, 139, 0.72);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.login-content {
  padding-left: 2px;
}

.oauth-chip {
  min-width: 58px;
  justify-content: center;
}

.oauth-item {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(201, 169, 98, 0.05);
  border: 1px solid rgba(201, 169, 98, 0.14);
}

/* ── 계좌 카드 ── */
.account-card {
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(201, 169, 98, 0.05);
  border: 1px solid rgba(201, 169, 98, 0.14);
  transition: background 0.15s;
}

.account-card:hover {
  background: rgba(201, 169, 98, 0.08);
}

.account-no {
  font-family: 'Courier New', monospace;
  letter-spacing: 0.04em;
}

/* ── 자산 카드 ── */
.asset-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  border-radius: 16px;
  text-align: center;
  min-height: 140px;
  height: 100%;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.asset-card--gold {
  background: linear-gradient(145deg, rgba(205, 178, 122, 0.15), rgba(205, 178, 122, 0.04));
  border: 1px solid rgba(205, 178, 122, 0.35);
}

.asset-card--blue {
  background: linear-gradient(145deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.03));
  border: 1px solid rgba(33, 150, 243, 0.22);
}

.asset-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(227, 213, 176, 0.8);
  margin-bottom: 6px;
}

.asset-amount {
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1.2;
}

.asset-unit {
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 2px;
}

.asset-amount--gold {
  color: #e2c17a;
}

.asset-amount--silver {
  color: #9dc7ff;
}

.asset-subtext {
  margin-top: 4px;
  font-size: 0.72rem;
  color: rgba(203, 191, 162, 0.9);
}

.history-list-surface {
  background: transparent;
}

.recent-table :deep(thead th) {
  color: #cdb27a !important;
  font-size: 12px;
  font-weight: 700;
}

.recent-table :deep(tbody td) {
  color: #efe5cf !important;
  font-size: 12px;
}

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 빈 상태 ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
}
</style>
